import React, { useEffect, useMemo, useState } from "react";
import { api, formatDetail, fmtDate, fmtEUR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileSignature, Plus, Trash2, RefreshCw, X, Check, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const FREQ_LABEL = { monthly: "monatlich", quarterly: "quartalsweise", yearly: "jährlich" };

export default function Contracts() {
  const { has, allowed_company_ids } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState({ fix: [], all: [] });
  const [companyFilter, setCompanyFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);          // contract for detail modal
  const [matches, setMatches] = useState([]);

  const canEdit = has("receipt.edit");

  const load = async () => {
    try {
      const [cRes, coRes, catRes] = await Promise.all([
        api.get("/contracts", { params: companyFilter ? { company_id: companyFilter } : {} }),
        api.get("/companies"),
        api.get("/contracts/categories"),
      ]);
      setContracts(cRes.data);
      setCompanies(coRes.data);
      setCategories(catRes.data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyFilter]);

  const grouped = useMemo(() => {
    const byCompany = {};
    for (const c of contracts) {
      (byCompany[c.company_id] ||= []).push(c);
    }
    return byCompany;
  }, [contracts]);

  const companyName = (id) => companies.find((x) => x.id === id)?.name || "—";

  const openDetail = async (c) => {
    setDetail(c);
    try {
      const { data } = await api.get(`/contracts/${c.id}/matches`);
      setMatches(data);
    } catch {
      setMatches([]);
    }
  };

  const rematch = async (c) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/rematch`);
      toast.success(`${data.matched} Buchung(en) gematcht`);
      load();
      if (detail?.id === c.id) openDetail(c);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Vertrag \u201E${c.name}\u201C wirklich löschen? Zugehörige Buchungen werden wieder als \u201EFehlender Beleg\u201C angezeigt.`)) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Vertrag gelöscht");
      if (detail?.id === c.id) setDetail(null);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="p-8 text-[#5C5E60]">Lade Verträge…</div>;

  return (
    <div className="px-8 py-8 space-y-6" data-testid="contracts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5E60] font-semibold">Buchhaltung</div>
          <h1 className="font-display font-black text-3xl text-[#1A1A1A]">Verträge</h1>
          <p className="text-sm text-[#5C5E60] mt-1 max-w-2xl">
            Wiederkehrende Buchungen (Miete, Leasing, Versicherungen, Abos) über hinterlegte Verträge abdecken. Passende Kontoauszug-Buchungen werden automatisch dem Vertrag zugeordnet und tauchen nicht als &bdquo;Fehlender Beleg&ldquo; auf.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="contract-new-btn"
            className="inline-flex items-center gap-2 bg-[#18322B] text-white px-4 py-2 rounded-md text-sm hover:bg-[#21453B]">
            <Plus className="h-4 w-4" /> Neuer Vertrag
          </button>
        )}
      </div>

      {/* Company filter */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[#5C5E60]">Firma:</span>
        <button
          onClick={() => setCompanyFilter("")}
          className={`px-3 py-1 rounded-md border ${!companyFilter ? "bg-[#18322B] text-white border-[#18322B]" : "border-[#E6E6E4] text-[#1A1A1A] hover:bg-[#F3F3F1]"}`}>
          Alle
        </button>
        {companies.filter((c) => !allowed_company_ids?.length || allowed_company_ids.includes(c.id)).map((c) => (
          <button
            key={c.id}
            onClick={() => setCompanyFilter(c.id)}
            className={`px-3 py-1 rounded-md border ${companyFilter === c.id ? "bg-[#18322B] text-white border-[#18322B]" : "border-[#E6E6E4] text-[#1A1A1A] hover:bg-[#F3F3F1]"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {contracts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E6E6E4] bg-[#FAFAF8] p-10 text-center">
          <FileSignature className="h-8 w-8 mx-auto text-[#C6C6C4] mb-3" strokeWidth={1.5} />
          <div className="font-semibold text-[#1A1A1A] mb-1">Noch keine Verträge angelegt</div>
          <div className="text-sm text-[#5C5E60]">Lege deinen ersten Vertrag an, um wiederkehrende Buchungen automatisch abzudecken.</div>
        </div>
      ) : (
        Object.entries(grouped).map(([cid, list]) => (
          <section key={cid} className="space-y-2">
            <div className="text-sm font-semibold text-[#1A1A1A]">{companyName(cid)} — {list.length} Vertrag/Verträge</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.map((c) => <ContractCard key={c.id} c={c} onOpen={() => openDetail(c)} onRematch={() => rematch(c)} onDelete={() => del(c)} canEdit={canEdit} />)}
            </div>
          </section>
        ))
      )}

      {showCreate && (
        <ContractCreateModal
          companies={companies.filter((c) => !allowed_company_ids?.length || allowed_company_ids.includes(c.id))}
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {detail && (
        <ContractDetailModal
          contract={detail}
          matches={matches}
          companyName={companyName(detail.company_id)}
          onClose={() => setDetail(null)}
          onRematch={() => rematch(detail)}
          onDelete={() => del(detail)}
          canEdit={canEdit}
          onReload={() => openDetail(detail)}
        />
      )}
    </div>
  );
}

function ContractCard({ c, onOpen, onRematch, onDelete, canEdit }) {
  return (
    <div className="bg-white border border-[#E6E6E4] rounded-md p-4 hover:border-[#C6C6C4] transition-colors" data-testid={`contract-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">{c.category}</div>
          <div className="font-semibold text-[#1A1A1A] truncate">{c.name}</div>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded ${c.direction === "outgoing" ? "bg-[#FFE1E1] text-[#8B1E1E]" : "bg-[#E4F0E8] text-[#18322B]"}`}>
          {c.direction === "outgoing" ? "Ausgang" : "Eingang"}
        </span>
      </div>
      <div className="text-2xl font-semibold text-[#18322B]">{fmtEUR(c.amount)}</div>
      <div className="text-xs text-[#5C5E60] mt-0.5">
        {FREQ_LABEL[c.frequency] || c.frequency}
        {c.day_of_period ? ` · Tag ${c.day_of_period}` : ""}
      </div>
      {c.counterparty_pattern && (
        <div className="text-xs text-[#5C5E60] mt-2 truncate">Suchmuster: <span className="font-mono">{c.counterparty_pattern}</span></div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F0EE]">
        <div className="text-xs text-[#5C5E60]"><b className="text-[#18322B]">{c.match_count ?? 0}</b> Buchung(en) zugeordnet</div>
        <div className="flex items-center gap-1">
          <button onClick={onOpen} data-testid={`contract-open-${c.id}`} className="text-xs px-2 py-1 rounded hover:bg-[#F3F3F1]"><ExternalLink className="h-3.5 w-3.5" /></button>
          {canEdit && <button onClick={onRematch} title="Neu abgleichen" className="text-xs px-2 py-1 rounded hover:bg-[#F3F3F1]"><RefreshCw className="h-3.5 w-3.5" /></button>}
          {canEdit && <button onClick={onDelete} title="Löschen" className="text-xs px-2 py-1 rounded hover:bg-[#FFE1E1] text-[#8B1E1E]"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
    </div>
  );
}

function ContractCreateModal({ companies, categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    company_id: companies[0]?.id || "",
    name: "", category: "Miete",
    amount: "", amount_tolerance_down: 1, amount_tolerance_up: 50,
    frequency: "monthly", day_of_period: 3, direction: "outgoing",
    counterparty_pattern: "", counterparty_iban: "",
    start_date: new Date().toISOString().slice(0, 10), end_date: "",
    notes: "",
  });
  const [customCat, setCustomCat] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_id || !form.name || !form.amount) {
      toast.error("Firma, Name und Betrag sind Pflicht");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, category: customCat.trim() || form.category }).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) fd.append(k, String(v));
      });
      if (file) fd.append("file", file);
      const { data } = await api.post("/contracts", fd);
      toast.success(`Vertrag angelegt — ${data.initial_matches ?? 0} Buchung(en) sofort zugeordnet`);
      onCreated();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Anlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg border border-[#E6E6E4] w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="contract-create-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">Neuer Vertrag</div>
            <h2 className="font-display font-bold text-xl text-[#1A1A1A]">Wiederkehrende Buchungen abdecken</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#5C5E60] hover:text-[#1A1A1A]"><X className="h-5 w-5" /></button>
        </div>

        <Field label="Firma *">
          <select value={form.company_id} onChange={update("company_id")} data-testid="contract-company"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *">
            <input value={form.name} onChange={update("name")} data-testid="contract-name"
                   placeholder="Miete Büro Berlin"
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
          <Field label="Kategorie">
            <select value={form.category} onChange={update("category")} data-testid="contract-category"
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
              {categories.all.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              <option value="__custom__">Eigene…</option>
            </select>
            {form.category === "__custom__" && (
              <input value={customCat} onChange={(e) => setCustomCat(e.target.value)}
                     placeholder="Eigene Kategorie eingeben"
                     className="w-full mt-2 px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Betrag € *">
            <input type="number" step="0.01" value={form.amount} onChange={update("amount")} data-testid="contract-amount"
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
          <Field label="Richtung">
            <select value={form.direction} onChange={update("direction")}
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
              <option value="outgoing">Ausgang (Soll)</option>
              <option value="incoming">Eingang (Haben)</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Toleranz nach unten (Rundung)">
            <input type="number" step="0.01" value={form.amount_tolerance_down} onChange={update("amount_tolerance_down")}
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
          <Field label="Toleranz nach oben (Mahngebühr)">
            <input type="number" step="0.01" value={form.amount_tolerance_up} onChange={update("amount_tolerance_up")}
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Frequenz">
            <select value={form.frequency} onChange={update("frequency")}
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
              <option value="monthly">monatlich</option>
              <option value="quarterly">quartalsweise</option>
              <option value="yearly">jährlich</option>
            </select>
          </Field>
          <Field label="Tag im Zeitraum (optional)">
            <input type="number" min="1" max="31" value={form.day_of_period || ""} onChange={update("day_of_period")}
                   placeholder="z. B. 3 (= 3. jeden Monats)"
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
        </div>

        <Field label="Gegenpartei-Suchmuster (Text im Verwendungszweck)">
          <input value={form.counterparty_pattern} onChange={update("counterparty_pattern")} data-testid="contract-pattern"
                 placeholder='z. B. "Musterhaus Immobilien"'
                 className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono" />
        </Field>

        <Field label="Gegenpartei-IBAN (optional, präziser)">
          <input value={form.counterparty_iban} onChange={update("counterparty_iban")}
                 placeholder="DE00 0000 0000 0000 0000 00"
                 className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Vertrag gültig ab">
            <input type="date" value={form.start_date} onChange={update("start_date")}
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
          <Field label="Vertrag gültig bis (optional)">
            <input type="date" value={form.end_date} onChange={update("end_date")}
                   className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
          </Field>
        </div>

        <Field label="Notizen (optional)">
          <textarea value={form.notes} onChange={update("notes")} rows={2}
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
        </Field>

        <Field label="Vertrags-PDF (optional)">
          <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="w-full text-sm" />
          <div className="text-[11px] text-[#5C5E60] mt-1">Wird im PDF-Export als Anhang mitgeliefert.</div>
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E6E6E4]">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-[#5C5E60] hover:text-[#1A1A1A]">Abbrechen</button>
          <button type="submit" disabled={busy} data-testid="contract-create-submit"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#18322B] text-white rounded-md text-sm hover:bg-[#21453B] disabled:opacity-50">
            <Check className="h-4 w-4" /> {busy ? "Speichert…" : "Anlegen"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContractDetailModal({ contract, matches, companyName, onClose, onRematch, onDelete, canEdit, onReload }) {
  const confirm = async (tx, accept) => {
    try {
      await api.post(`/transactions/${tx.id}/confirm-contract`, { accept });
      toast.success(accept ? "Abweichung bestätigt" : "Zuordnung aufgehoben");
      onReload();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-lg border border-[#E6E6E4] w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto"
           data-testid="contract-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">{companyName} · {contract.category}</div>
            <h2 className="font-display font-bold text-xl text-[#1A1A1A]">{contract.name}</h2>
            <div className="text-sm text-[#5C5E60] mt-1">
              {fmtEUR(contract.amount)} · {FREQ_LABEL[contract.frequency]}
              {contract.day_of_period ? ` · Tag ${contract.day_of_period}` : ""}
              {" · "}Toleranz: −{contract.amount_tolerance_down}€ / +{contract.amount_tolerance_up}€
            </div>
          </div>
          <button onClick={onClose} className="text-[#5C5E60] hover:text-[#1A1A1A]"><X className="h-5 w-5" /></button>
        </div>

        {contract.storage_path && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contract.id}/file`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#E6E6E4] hover:bg-[#F3F3F1] mb-3">
            <ExternalLink className="h-3.5 w-3.5" /> Vertragsdokument öffnen
          </a>
        )}

        {contract.notes && (
          <div className="text-sm text-[#1A1A1A] bg-[#F9F9F8] border border-[#E6E6E4] rounded p-3 mb-4">
            {contract.notes}
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-[#1A1A1A]">Zugeordnete Buchungen ({matches.length})</h3>
          {canEdit && (
            <button onClick={onRematch} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-[#F3F3F1]">
              <RefreshCw className="h-3.5 w-3.5" /> Neu abgleichen
            </button>
          )}
        </div>

        {matches.length === 0 ? (
          <div className="text-sm text-[#5C5E60] italic">Noch keine Buchungen zugeordnet.</div>
        ) : (
          <div className="overflow-x-auto rounded border border-[#E6E6E4]">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F3F3F1] text-[10px] uppercase tracking-wider text-[#5C5E60]">
                <tr>
                  <th className="px-3 py-2 text-left">Datum</th>
                  <th className="px-3 py-2 text-left">Verwendungszweck</th>
                  <th className="px-3 py-2 text-right">Betrag</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  {canEdit && <th className="px-3 py-2 text-right">Aktion</th>}
                </tr>
              </thead>
              <tbody>
                {matches.map((t) => (
                  <tr key={t.id} className="border-t border-[#F0F0EE]">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(t.booking_date)}</td>
                    <td className="px-3 py-2">{t.counterparty || t.description || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEUR(t.amount)}</td>
                    <td className="px-3 py-2">
                      {t.contract_match_flag === "abweichung" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-[#FFF4E5] text-[#915000]">
                          <AlertTriangle className="h-3 w-3" /> Abweichung
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-[#E4F0E8] text-[#18322B]">OK</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        {t.contract_match_flag === "abweichung" && (
                          <div className="inline-flex gap-1">
                            <button onClick={() => confirm(t, true)} className="text-xs px-2 py-0.5 rounded bg-[#E4F0E8] text-[#18322B]" title="Abweichung ist ok (z. B. Mahngebühr)">
                              <Check className="h-3 w-3" />
                            </button>
                            <button onClick={() => confirm(t, false)} className="text-xs px-2 py-0.5 rounded bg-[#FFE1E1] text-[#8B1E1E]" title="Zuordnung aufheben">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canEdit && (
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#E6E6E4]">
            <button onClick={onDelete} className="text-xs text-[#8B1E1E] hover:underline flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" /> Vertrag löschen
            </button>
            <button onClick={onClose} className="text-xs text-[#5C5E60] hover:text-[#1A1A1A]">Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
